import { create } from 'zustand';

export const COLOR_MODE_KEY = 'color-mode';

/** Surface tokens applied in dark mode (brand accents stay from active theme). */
export const DARK_SURFACE_COLORS = {
  background: '160 18% 7%',
  foreground: '0 0% 96%',
  muted: '160 12% 14%',
  'muted-foreground': '160 8% 65%',
  popover: '160 14% 10%',
  'popover-foreground': '0 0% 96%',
  card: '160 14% 10%',
  'card-foreground': '0 0% 96%',
  border: '160 10% 20%',
  input: '160 10% 20%',
  accent: '160 12% 16%',
  'accent-foreground': '0 0% 96%',
  ring: '160 30% 40%',
};

export const DARK_GRADIENTS = {
  body: 'linear-gradient(270deg, rgba(4, 60, 45, 0.92) -15.61%, #062820 51.49%)',
  sidebar: 'linear-gradient(0deg, #063528 36.73%, rgba(6, 53, 40, 0.92) 93.78%)',
};

const LIGHT_SURFACE_KEYS = Object.keys(DARK_SURFACE_COLORS);

/**
 * Resolve stored preference to effective light/dark.
 * @param {'light'|'dark'|'system'} mode
 */
export function resolveColorMode(mode) {
  if (mode === 'dark') return 'dark';
  if (mode === 'system') {
    try {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    } catch {
      return 'light';
    }
  }
  return 'light';
}

function loadStoredMode() {
  try {
    const stored = localStorage.getItem(COLOR_MODE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
  } catch {}
  return 'light';
}

/**
 * Apply or remove the `.dark` class and related meta for native UI.
 */
export function applyColorModeClass(resolved) {
  const root = document.documentElement;
  const isDark = resolved === 'dark';
  root.classList.toggle('dark', isDark);
  root.style.colorScheme = isDark ? 'dark' : 'light';

  const colorSchemeMeta = document.querySelector('meta[name="color-scheme"]');
  if (colorSchemeMeta) {
    colorSchemeMeta.setAttribute('content', isDark ? 'dark light' : 'light');
  }

  const themeColorMeta = document.querySelector('meta[name="theme-color"]');
  if (themeColorMeta) {
    themeColorMeta.setAttribute('content', isDark ? '#0a2f24' : '#0A4D39');
  }
}

/**
 * Overlay dark surface tokens / restore light surfaces from brand theme.
 */
export function applySurfaceTokens(resolved, brandTheme) {
  const root = document.documentElement;
  if (resolved === 'dark') {
    Object.entries(DARK_SURFACE_COLORS).forEach(([key, value]) => {
      root.style.setProperty(`--${key}`, value);
    });
    root.style.setProperty('--gradient-body', DARK_GRADIENTS.body);
    root.style.setProperty('--gradient-sidebar', DARK_GRADIENTS.sidebar);
    return;
  }

  const colors = brandTheme?.colors;
  if (colors && typeof colors === 'object') {
    LIGHT_SURFACE_KEYS.forEach((key) => {
      if (colors[key]) {
        root.style.setProperty(`--${key}`, colors[key]);
      }
    });
  }
  if (brandTheme?.gradients?.body) {
    root.style.setProperty('--gradient-body', brandTheme.gradients.body);
  }
  if (brandTheme?.gradients?.sidebar) {
    root.style.setProperty('--gradient-sidebar', brandTheme.gradients.sidebar);
  }
}

const useColorModeStore = create((set, get) => ({
  mode: loadStoredMode(),
  resolved: resolveColorMode(loadStoredMode()),

  setMode: (mode) => {
    const next = mode === 'dark' || mode === 'system' ? mode : 'light';
    try {
      localStorage.setItem(COLOR_MODE_KEY, next);
    } catch {}
    const resolved = resolveColorMode(next);
    set({ mode: next, resolved });
    return resolved;
  },

  toggle: () => {
    const { resolved } = get();
    return get().setMode(resolved === 'dark' ? 'light' : 'dark');
  },

  /** Re-resolve when OS preference changes (system mode only). */
  syncSystem: () => {
    const { mode } = get();
    if (mode !== 'system') return get().resolved;
    const resolved = resolveColorMode('system');
    set({ resolved });
    return resolved;
  },
}));

export default useColorModeStore;
